import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { functionError } from '@/hooks/useMix'
import { SEED_COSTS } from '@/hooks/useSeeds'
import { tossSeeds, useSeedFx } from '@/lib/seedFx'
import type { NewsletterItem } from '@/types/library'

/**
 * This week's newsletter issue: its items (Brian's ＋ Newsletter, article.html
 * v2.2, AI-HANDSHAKE §4). The AI writes each story's newsletter version (a
 * title and a 50 to 90 word attributed blurb, 1 seed) in the
 * newsletter-version function, which stores it against this issue week.
 *
 *   const add = useAddToNewsletter()
 *   add.mutate({ storyId })            // a built story
 *   add.mutate({ headlineId })         // a feed card that was never built
 *   // → { item, count, charged }      count = items in this week's issue
 *
 *   const { has } = useNewsletterItems()
 *   has({ storyId, headlineId })       // already in this week's issue?
 *   useRemoveFromNewsletter().mutate(item.id)
 */

export const NL_ITEMS_KEY = 'newsletter-items'

/** The Monday this account's issue week starts (its own timezone). */
export function useIssueWeek() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['newsletter-week', clientId],
    enabled: !!clientId,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('newsletter_issue_week')
      if (error) throw error
      return String(data)
    },
  })
}

/** This week's items, in issue order, and a lookup. */
export function useNewsletterItems() {
  const clientId = useEffectiveClientId()
  const { data: week } = useIssueWeek()
  const q = useQuery({
    queryKey: [NL_ITEMS_KEY, clientId, week],
    enabled: !!clientId && !!week,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('newsletter_items').select('*')
        .eq('client_id', clientId!).eq('issue_week', week!)
        .order('position', { ascending: true })
      if (error) throw error
      return (data ?? []) as NewsletterItem[]
    },
  })
  const items = q.data ?? []
  const find = (ref: { storyId?: string | null; headlineId?: string | null }) =>
    items.find((i) =>
      (!!ref.storyId && i.story_id === ref.storyId) ||
      (!!ref.headlineId && i.headline_id === ref.headlineId)) ?? null
  return { ...q, items, week, find, has: (ref: { storyId?: string | null; headlineId?: string | null }) => !!find(ref) }
}

export interface AddToNewsletterArgs {
  storyId?: string | null
  headlineId?: string | null
}

export interface AddToNewsletterResult {
  item: NewsletterItem
  /** Items in this week's issue, this one included. */
  count: number
  /** 1 when the AI wrote it, 0 when it was already in. */
  charged: number
}

/**
 * ＋ Newsletter: write the story's newsletter version (1 seed) and add it to
 * this week's issue. Already in: returns it, charges nothing. A failed write
 * costs nothing. Errors carry `status` (402 when the week can't cover it).
 */
export function useAddToNewsletter() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  const releaseHold = useSeedFx((s) => s.release)
  return useMutation<AddToNewsletterResult, Error & { status?: number }, AddToNewsletterArgs>({
    mutationFn: async ({ storyId, headlineId }) => {
      if (!storyId && !headlineId) throw new Error('Nothing to add.')
      tossSeeds(SEED_COSTS.newsletter_version)
      const { data, error } = await supabase.functions.invoke('newsletter-version', {
        body: storyId ? { story_id: storyId } : { headline_id: headlineId },
      })
      if (error) { releaseHold(); throw await functionError(error) }
      const out = data as AddToNewsletterResult
      if (!out.charged) releaseHold()
      return out
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NL_ITEMS_KEY, clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-history', clientId] })
      queryClient.invalidateQueries({ queryKey: ['library', clientId] })
      queryClient.invalidateQueries({ queryKey: ['feed-newsletter', clientId] })
    },
  })
}

/** Take an item out of this week's issue. The seed is not given back. */
export function useRemoveFromNewsletter() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase.from('newsletter_items').delete().eq('id', itemId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NL_ITEMS_KEY, clientId] })
      queryClient.invalidateQueries({ queryKey: ['library', clientId] })
      queryClient.invalidateQueries({ queryKey: ['feed-newsletter', clientId] })
    },
  })
}
